// src/pages/hr/recruitment/PipelineKanban.jsx
import React, { useState } from "react";
import { useRecruitmentContext } from "../../../context/RecruitmentContext";
import { updateCandidateStage } from "../../../hooks/useRecruitment";
import { useAuth } from "../../../context/AuthContext";
import CandidateProfile from "./CandidateProfile";
import { toast } from "react-hot-toast";
import {
  Users,
  Search,
  FileText,
  Building2,
  Calendar,
  Sparkles,
  ChevronRight,
  ShieldCheck,
} from "lucide-react";

export const KANBAN_STAGES = [
  {
    id: "Application",
    step: "01",
    title: "Application",
    subtitle: "Submit your details & resume",
  },
  {
    id: "Screening",
    step: "02",
    title: "Screening",
    subtitle: "Quick call with talent team",
  },
  {
    id: "Technical Round",
    step: "03",
    title: "Technical Round",
    subtitle: "Skills + problem-solving discussion",
  },
  {
    id: "Culture Fit",
    step: "04",
    title: "Culture Fit",
    subtitle: "Meet the team & leadership",
  },
  {
    id: "Offer",
    step: "05",
    title: "Offer",
    subtitle: "Exciting opportunity awaits!",
  },
];

export function mapStageToKanban(candStage = "") {
  const s = candStage.toLowerCase().trim();
  if (s === "screening" || s.includes("screen") || s.includes("shortlist")) {
    return "Screening";
  }
  if (s.includes("tech") || s.includes("assessment") || s.includes("interview") || s.includes("lead")) {
    return "Technical Round";
  }
  if (
    s.includes("culture") ||
    s.includes("manager") ||
    s.includes("admin review") ||
    s.includes("hr review") ||
    s.includes("pending hr")
  ) {
    return "Culture Fit";
  }
  if (s.includes("offer") || s.includes("hired") || s.includes("onboard") || s === "offer letter generated" || s === "offer sent") {
    return "Offer";
  }
  return "Application";
}

export default function PipelineKanban() {
  const { candidates, candidatesLoading } = useRecruitmentContext();
  const { userProfile } = useAuth();

  const [draggedCandidateId, setDraggedCandidateId] = useState(null);
  const [dragOverStage, setDragOverStage] = useState(null);
  const [selectedCandidate, setSelectedCandidate] = useState(null);
  const [search, setSearch] = useState("");

  const filteredCandidates = candidates.filter((c) => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (
      (c.name || "").toLowerCase().includes(q) ||
      (c.appliedPosition || c.role || "").toLowerCase().includes(q) ||
      (c.department || "").toLowerCase().includes(q)
    );
  });

  function handleDragStart(e, candidateId) {
    e.dataTransfer.setData("text/plain", candidateId);
    setDraggedCandidateId(candidateId);
  }

  function handleDragOver(e, stageId) {
    e.preventDefault();
    if (dragOverStage !== stageId) {
      setDragOverStage(stageId);
    }
  }

  function handleDragLeave(e, stageId) {
    if (dragOverStage === stageId) {
      setDragOverStage(null);
    }
  }

  async function handleDrop(e, targetStage) {
    e.preventDefault();
    setDragOverStage(null);
    const candidateId = e.dataTransfer.getData("text/plain") || draggedCandidateId;
    if (!candidateId) return;

    const cand = candidates.find((c) => c.id === candidateId);
    if (cand) {
      const currentGroup = mapStageToKanban(cand.stage);
      if (currentGroup !== targetStage) {
        try {
          await updateCandidateStage(
            candidateId,
            targetStage,
            userProfile?.displayName || userProfile?.name || "HR Admin",
            cand.jobId
          );
          toast.success(`Moved ${cand.name} to ${targetStage}`);
        } catch (err) {
          toast.error("Failed to move candidate: " + err.message);
        }
      }
    }
    setDraggedCandidateId(null);
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "1.25rem" }}>
      <style>{`
        .kanban-main-wrapper {
          overflow-x: auto;
          padding: 0.25rem 0 1rem;
        }
        .kanban-flow-container {
          display: grid;
          grid-template-columns: repeat(5, minmax(180px, 1fr));
          gap: 0.65rem;
          width: 100%;
        }
        @media (max-width: 1050px) {
          .kanban-flow-container {
            min-width: 950px;
          }
        }
        .kanban-step-column {
          background: #ffffff;
          border: 1px solid #e2e8f0;
          border-radius: 14px;
          box-shadow: 0 2px 10px rgba(0, 0, 0, 0.03);
          display: flex;
          flex-direction: column;
          padding: 0.85rem 0.65rem;
          transition: all 0.2s ease;
          position: relative;
        }
        .kanban-step-column.drag-hover {
          border-color: #10b981;
          box-shadow: 0 6px 20px rgba(16, 185, 129, 0.15);
          background: #f0fdf4;
        }
        .step-number-badge {
          width: 38px;
          height: 28px;
          border-radius: 8px;
          background: #1b4b31;
          color: #ffffff;
          font-weight: 800;
          font-size: 0.84rem;
          display: flex;
          align-items: center;
          justify-content: center;
          margin: 0 auto 0.4rem;
          box-shadow: 0 2px 6px rgba(27, 75, 49, 0.2);
        }
        .step-title-text {
          font-size: 0.92rem;
          font-weight: 800;
          color: #0f172a;
          text-align: center;
          margin-bottom: 0.15rem;
        }
        .step-subtitle-text {
          font-size: 0.68rem;
          color: #64748b;
          text-align: center;
          line-height: 1.25;
          margin-bottom: 0.45rem;
        }
        .step-count-pill {
          background: #f0fdf4;
          color: #065f46;
          border: 1px solid #bbf7d0;
          border-radius: 99px;
          padding: 0.15rem 0.5rem;
          font-size: 0.68rem;
          font-weight: 700;
          display: inline-flex;
          align-items: center;
          gap: 0.25rem;
          margin: 0 auto 0.65rem;
        }
        .kanban-cards-area {
          flex: 1;
          min-height: 120px;
          max-height: 480px;
          overflow-y: auto;
          display: flex;
          flex-direction: column;
          gap: 0.55rem;
          padding: 0.15rem 0.05rem;
        }
        .candidate-drag-card {
          background: #ffffff;
          border: 1px solid #e2e8f0;
          border-radius: 10px;
          padding: 0.65rem 0.75rem;
          box-shadow: 0 1px 4px rgba(0, 0, 0, 0.03);
          cursor: grab;
          transition: all 0.15s ease;
          display: flex;
          flex-direction: column;
          gap: 0.3rem;
        }
        .candidate-drag-card:hover {
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(15, 23, 42, 0.08);
          border-color: #cbd5e1;
        }
        .candidate-drag-card:active {
          cursor: grabbing;
        }
        .cand-card-name {
          font-weight: 800;
          font-size: 0.85rem;
          color: #0f172a;
        }
        .cand-card-role {
          font-size: 0.72rem;
          color: #475569;
          font-weight: 600;
        }
        .cand-card-dept {
          font-size: 0.68rem;
          color: #1b4b31;
          font-weight: 700;
        }
      `}</style>

      {/* Toolbar & Search */}
      <div
        style={{
          background: "#ffffff",
          border: "1px solid #e2e8f0",
          borderRadius: 14,
          padding: "0.75rem 1rem",
          display: "flex",
          justifyContent: "flex-end",
          alignItems: "center",
          gap: "1rem",
        }}
      >
        <div
          style={{
            position: "relative",
            display: "flex",
            alignItems: "center",
            width: "100%",
            maxWidth: 360,
            background: "#f8fafc",
            border: "1.5px solid #cbd5e1",
            borderRadius: 10,
            padding: "0 0.75rem",
            height: 38,
          }}
        >
          <Search size={16} style={{ color: "#64748b", marginRight: "0.5rem", flexShrink: 0 }} />
          <input
            type="text"
            style={{
              width: "100%",
              border: "none",
              outline: "none",
              background: "transparent",
              fontSize: "0.82rem",
              fontWeight: 500,
              color: "#0f172a",
            }}
            placeholder="Search candidates in pipeline..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
      </div>

      {/* 5-Step Pipeline Flow Kanban Board */}
      {candidatesLoading ? (
        <div style={{ textAlign: "center", padding: "4rem", color: "var(--text-muted)", fontSize: "0.9rem" }}>
          Loading Pipeline Stages...
        </div>
      ) : (
        <div className="kanban-main-wrapper">
          <div className="kanban-flow-container">
            {KANBAN_STAGES.map((step, idx) => {
              const stageCandidates = filteredCandidates.filter(
                (c) => mapStageToKanban(c.stage) === step.id
              );
              const isDragOver = dragOverStage === step.id;

              return (
                <div
                  key={step.id}
                  className={`kanban-step-column ${isDragOver ? "drag-hover" : ""}`}
                  onDragOver={(e) => handleDragOver(e, step.id)}
                  onDragLeave={(e) => handleDragLeave(e, step.id)}
                  onDrop={(e) => handleDrop(e, step.id)}
                >
                  {/* Step Number Badge */}
                  <div className="step-number-badge">{step.step}</div>

                    {/* Step Title */}
                    <div className="step-title-text">{step.title}</div>

                    {/* Step Subtitle */}
                    <div className="step-subtitle-text">{step.subtitle}</div>

                    {/* Candidate Count Pill */}
                    <div className="step-count-pill">
                      <Users size={12} /> {stageCandidates.length} Candidates
                    </div>

                    {/* Cards Container */}
                    <div className="kanban-cards-area">
                      {stageCandidates.length === 0 ? (
                        <div
                          style={{
                            border: "1.5px dashed #cbd5e1",
                            borderRadius: 10,
                            padding: "0.85rem 0.5rem",
                            textAlign: "center",
                            color: "#94a3b8",
                            fontSize: "0.72rem",
                            fontStyle: "italic",
                            margin: "auto 0",
                          }}
                        >
                          Drop candidates here
                        </div>
                      ) : (
                        stageCandidates.map((cand) => (
                          <div
                            key={cand.id}
                            className="candidate-drag-card"
                            draggable
                            onDragStart={(e) => handleDragStart(e, cand.id)}
                            onClick={() => setSelectedCandidate(cand)}
                          >
                            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: "0.4rem" }}>
                              <div className="cand-card-name">{cand.name}</div>
                              {cand.technicalRating && (
                                <span
                                  style={{
                                    fontSize: "0.68rem",
                                    fontWeight: 800,
                                    color: "#166534",
                                    background: "#dcfce7",
                                    padding: "0.1rem 0.35rem",
                                    borderRadius: 6,
                                  }}
                                >
                                  ★ {cand.technicalRating}/10
                                </span>
                              )}
                            </div>

                            <div className="cand-card-role">
                              {cand.appliedPosition || cand.role || "Applicant"}
                            </div>

                            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 4 }}>
                              <span className="cand-card-dept">{cand.department || "General"}</span>
                              <span
                                style={{
                                  fontSize: "0.68rem",
                                  color: "#0369a1",
                                  background: "#f0f9ff",
                                  border: "1px solid #bae6fd",
                                  padding: "1px 6px",
                                  borderRadius: 4,
                                  fontWeight: 700,
                                }}
                              >
                                {cand.experience || "Fresher"}
                              </span>
                            </div>

                            {cand.adminDecision && (
                              <div style={{ fontSize: "0.68rem", color: "#6d28d9", fontWeight: 700, marginTop: 2 }}>
                                Admin: {cand.adminDecision}
                              </div>
                            )}
                          </div>
                        ))
                      )}
                    </div>
                  </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Candidate Profile Modal */}
      {selectedCandidate && (
        <CandidateProfile
          candidate={selectedCandidate}
          onClose={() => setSelectedCandidate(null)}
        />
      )}
    </div>
  );
}
